import React, { useState } from 'react';
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Button,
  Alert,
  Card,
  CardContent,
  LinearProgress,
} from '@mui/material';

const questions = [
  {
    id: 1,
    question: "What is the first step in implementing a new fall prevention protocol?",
    options: [
      "Purchase new equipment",
      "Staff training and education",
      "Environmental modifications",
      "Resident assessment"
    ],
    correct: "Staff training and education",
    explanation: "Staff training ensures consistent implementation and understanding of new protocols across all care providers."
  },
  {
    id: 2,
    question: "How should progress in fall prevention programs be monitored?",
    options: [
      "Annual reviews only",
      "Weekly incident reports",
      "Daily observation logs",
      "Continuous monitoring with regular assessments"
    ],
    correct: "Continuous monitoring with regular assessments",
    explanation: "Continuous monitoring allows for timely identification of issues and adjustment of interventions as needed."
  },
  {
    id: 3,
    question: "What is the most effective way to ensure compliance with fall prevention protocols?",
    options: [
      "Written warnings for non-compliance",
      "Regular audits and feedback",
      "Increased supervision",
      "Monetary incentives"
    ],
    correct: "Regular audits and feedback",
    explanation: "Regular audits with constructive feedback promote learning and improvement while ensuring protocol adherence."
  },
  {
    id: 4,
    question: "How should new fall prevention strategies be introduced to residents?",
    options: [
      "Implement all changes at once",
      "Gradual implementation with education",
      "Written notices only",
      "Wait for residents to request changes"
    ],
    correct: "Gradual implementation with education",
    explanation: "Gradual implementation with proper education helps residents adapt to changes and understand their importance."
  }
];

const ImplementationQuiz = ({ onComplete }) => {
  const [hasCalledComplete, setHasCalledComplete] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);

  const handleAnswer = (event) => {
    setAnswers({
      ...answers,
      [currentQuestion]: event.target.value
    });
    setShowExplanation(true);
  };

  const handleNext = () => {
    setShowExplanation(false);
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResult(true);
    }
  };

  const calculateScore = () => {
    let score = 0;
    Object.keys(answers).forEach(questionIndex => {
      if (answers[questionIndex] === questions[questionIndex].correct) {
        score++;
      }
    });
    return (score / questions.length) * 100;
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResult(false);
    setShowExplanation(false);
    setHasCalledComplete(false);
  };

  if (showResult) {
    const score = calculateScore();
    if (score >= 70 && onComplete && !hasCalledComplete) {
      setHasCalledComplete(true);
      onComplete();
    }
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Implementation Knowledge Check Complete!</Typography>
          <Typography variant="body1" gutterBottom>
            Your score: {score}%
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={score} 
            sx={{ my: 2, height: 10, borderRadius: 5 }}
          />
          {score >= 70 ? (
            <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
              Congratulations! You've passed the quiz. You can now proceed to the next section.
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
              You need a score of 70% or higher to proceed. Please try again.
            </Alert>
          )}
          <Button 
            variant="contained" 
            onClick={resetQuiz}
            sx={{ mt: 2 }}
          >
            Retake Quiz
          </Button>
        </CardContent>
      </Card>
    );
  }

  const question = questions[currentQuestion];

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Implementation Strategies Quiz
        </Typography>
        <LinearProgress 
          variant="determinate" 
          value={(currentQuestion / questions.length) * 100} 
          sx={{ mb: 3, height: 10, borderRadius: 5 }}
        />
        
        <Typography variant="body1" gutterBottom>
          Question {currentQuestion + 1} of {questions.length}
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          {question.question}
        </Typography>

        <FormControl component="fieldset">
          <RadioGroup
            value={answers[currentQuestion] || ''}
            onChange={handleAnswer}
          >
            {question.options.map((option, index) => (
              <FormControlLabel
                key={index}
                value={option}
                control={<Radio />}
                label={option}
              />
            ))}
          </RadioGroup>
        </FormControl>

        {showExplanation && (
          <Alert 
            severity={answers[currentQuestion] === question.correct ? "success" : "error"}
            sx={{ mt: 2, mb: 2 }}
          >
            {answers[currentQuestion] === question.correct ? "Correct! " : "Incorrect. "}
            {question.explanation}
          </Alert>
        )}

        <Button
          variant="contained"
          onClick={handleNext}
          disabled={!answers[currentQuestion]}
          sx={{ mt: 2 }}
        >
          {currentQuestion < questions.length - 1 ? 'Next Question' : 'Show Results'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default ImplementationQuiz;
